/**
 * Quick Reaction Panel Component
 * Provides instant feedback options for users
 */

import React, { useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { FeedbackType, FeedbackWidgetConfig } from '../../types/feedback';

interface QuickReactionPanelProps {
  onReaction: (reaction: 'helpful' | 'confusing' | 'error' | 'improvement') => void;
  onFeedbackType: (type: FeedbackType) => void;
  onElementSelection?: () => void;
  onClose: () => void;
  onMinimize: () => void;
  position: FeedbackWidgetConfig['position'];
  theme: FeedbackWidgetConfig['theme'];
  isSwissTheme?: boolean;
}

const QuickReactionPanel: React.FC<QuickReactionPanelProps> = ({
  onReaction,
  onFeedbackType,
  onElementSelection,
  onClose,
  onMinimize,
  position,
  theme,
  isSwissTheme = false
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  // Animate in on mount
  useEffect(() => {
    if (panelRef.current) {
      panelRef.current.style.transform = 'scale(0.8) translateY(10px)';
      panelRef.current.style.opacity = '0';
      
      requestAnimationFrame(() => {
        if (panelRef.current) {
          panelRef.current.style.transition = 'all 0.3s ease-out';
          panelRef.current.style.transform = 'scale(1) translateY(0)';
          panelRef.current.style.opacity = '1';
        }
      });
    }
  }, []);

  const positionClasses = {
    'bottom-right': 'bottom-20 right-0',
    'bottom-left': 'bottom-20 left-0',
    'top-right': 'top-20 right-0',
    'top-left': 'top-20 left-0'
  };

  const reactions = [
    {
      key: 'helpful' as const,
      label: 'Helpful',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
        </svg>
      ),
      color: isSwissTheme ? 'text-teal-600 hover:text-teal-700 hover:bg-teal-50' : 'text-sage-600 hover:text-sage-700 hover:bg-sage-50'
    },
    {
      key: 'confusing' as const,
      label: 'Confusing',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r={10}/>
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
          <path d="M12 17h.01"/>
        </svg>
      ),
      color: isSwissTheme ? 'text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50' : 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'
    },
    {
      key: 'error' as const,
      label: 'Error',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r={10}/>
          <path d="M15 9l-6 6"/>
          <path d="M9 9l6 6"/>
        </svg>
      ),
      color: isSwissTheme ? 'text-red-600 hover:text-red-700 hover:bg-red-50' : 'text-coral-600 hover:text-coral-700 hover:bg-coral-50'
    },
    {
      key: 'improvement' as const,
      label: 'Suggestion',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r={3}/>
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
        </svg>
      ),
      color: isSwissTheme ? 'text-blue-600 hover:text-blue-700 hover:bg-blue-50' : 'text-steel-600 hover:text-steel-700 hover:bg-steel-50'
    }
  ];

  const feedbackTypes = [
    {
      key: 'page-comment' as const,
      label: 'Page Comment',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      )
    },
    {
      key: 'bug-report' as const,
      label: 'Report Bug',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M8 2v4a6 6 0 0 0 12 0V2"/>
          <path d="M14 2v4a6 6 0 0 1-6 0V2z"/>
          <path d="M6.8 8.4L4 10l2 4 2.8-1.6"/>
          <path d="M17.2 8.4L20 10l-2 4-2.8-1.6"/>
          <path d="M6 12h12"/>
          <path d="M8 18v4"/>
          <path d="M16 18v4"/>
        </svg>
      )
    },
    {
      key: 'feature-request' as const,
      label: 'Feature Request',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r={3}/>
          <path d="M12 1v6m0 6v6"/>
          <path d="M21 12h-6m-6 0H3"/>
        </svg>
      )
    }
  ];

  return (
    <div
      ref={panelRef}
      data-feedback-quick-reactions
      className={cn(
        'absolute z-[999999] bg-white shadow-xl p-4 min-w-[280px]',
        isSwissTheme ? 'border border-gray-300' : 'border border-stone-300 rounded-lg',
        positionClasses[position]
      )}
      style={{
        transformOrigin: position.includes('bottom') ? 'bottom' : 'top',
        zIndex: 999999,
        borderRadius: isSwissTheme ? '0' : undefined
      }}
    >
      {/* Header */}
      <div className={cn(
        "flex items-center justify-between mb-4 pb-3 border-b",
        isSwissTheme ? "border-gray-200" : "border-stone-200"
      )}>
        <h3 className={cn(
          "text-sm font-semibold",
          isSwissTheme ? "text-gray-900" : "text-slate-800"
        )}>Quick Feedback</h3>
        <div className="flex items-center gap-1">
          <button
            onClick={onMinimize}
            className="p-1 text-slate-400 hover:text-slate-600 transition-colors rounded"
            title="Minimize widget"
            aria-label="Minimize feedback widget"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </button>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 transition-colors rounded"
            title="Close panel"
            aria-label="Close feedback panel"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18"/>
              <path d="M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Quick Reactions */}
      <div className="mb-4">
        <h4 className={cn(
          "text-xs font-medium mb-3 uppercase tracking-wide",
          isSwissTheme ? "text-gray-600" : "text-slate-600"
        )}>
          Quick Response
        </h4>
        <div className="grid grid-cols-2 gap-2">
          {reactions.map((reaction) => (
            <button
              key={reaction.key}
              onClick={() => onReaction(reaction.key)}
              className={cn(
                'flex items-center gap-2 p-3 border',
                'transition-all duration-200 text-sm font-medium min-h-[44px] touch-manipulation',
                'hover:scale-[1.02] hover:shadow-sm',
                isSwissTheme ? 'border-gray-200' : 'rounded-lg border-stone-200',
                reaction.color
              )}
              style={{ borderRadius: isSwissTheme ? '0' : undefined }}
              aria-label={`Rate this page as ${reaction.label}`}
            >
              {reaction.icon}
              <span className="text-xs md:text-sm">{reaction.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* More Feedback Types */}
      <div className="mb-4">
        <h4 className={cn(
          "text-xs font-medium mb-3 uppercase tracking-wide",
          isSwissTheme ? "text-gray-600" : "text-slate-600"
        )}>
          Need More Detail?
        </h4>
        <div className="space-y-1">
          {feedbackTypes.map((type) => (
            <button
              key={type.key}
              onClick={() => onFeedbackType(type.key)}
              className={cn(
                "w-full flex items-center gap-3 p-3 text-left text-sm",
                "transition-colors min-h-[44px] touch-manipulation hover:scale-[1.01]",
                isSwissTheme
                  ? "text-gray-700 hover:bg-gray-50"
                  : "text-slate-700 hover:bg-stone-50 rounded-lg"
              )}
              style={{ borderRadius: isSwissTheme ? '0' : undefined }}
              aria-label={`Open ${type.label} form`}
            >
              {type.icon}
              <span className="text-xs md:text-sm">{type.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Element Selection */}
      {onElementSelection && (
        <div className={cn(
          "pt-3 border-t",
          isSwissTheme ? "border-gray-200" : "border-stone-200"
        )}>
          <button
            onClick={onElementSelection}
            className={cn(
              "w-full flex items-center justify-center gap-2 p-2 text-sm font-medium border transition-colors",
              isSwissTheme
                ? "text-blue-500 border-blue-500 hover:bg-blue-50"
                : "text-warm-500 border-warm-500 hover:bg-warm-50"
            )}
            style={{borderRadius: 0}}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 9l-4 4v3a2 2 0 0 0 2 2h3l4-4"/>
              <path d="M14 5l4 4-6.5 6.5a2 2 0 0 1-3-3L14 5z"/>
            </svg>
            Select Element to Comment
          </button>
        </div>
      )}

      {/* Keyboard Hint */}
      <div className={cn(
        "text-xs text-center mt-3 pt-3 border-t",
        isSwissTheme ? "text-gray-400 border-gray-200" : "text-slate-400 border-stone-200"
      )}>
        Press <kbd className={cn(
          "px-1 py-0.5",
          isSwissTheme ? "bg-gray-100 text-gray-600" : "bg-stone-100 rounded text-slate-600"
        )}>Esc</kbd> to close
      </div>
    </div>
  );
};

export default QuickReactionPanel;